'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Search, Pencil, Trash2, Pause, Play, Star, Eye, Unlock, Calendar, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import type { OwnerListing, OwnerListingStatus } from './mockOwnerData';
import { STATUS_CONFIG } from './mockOwnerData';
import DeleteConfirmModal from './DeleteConfirmModal';
import EditListingModal from './EditListingModal';

interface Props {
  listings: OwnerListing[];
  setListings: React.Dispatch<React.SetStateAction<OwnerListing[]>>;
}

type SortField = 'listedAt' | 'views' | 'unlocks' | 'price';
type SortDir = 'asc' | 'desc';

export default function ListingsTable({ listings, setListings }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OwnerListingStatus | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('listedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<OwnerListing | null>(null);
  const [editTarget, setEditTarget] = useState<OwnerListing | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filtered = listings
    .filter(l => {
      const matchSearch = l.title.includes(search) || l.city.includes(search) || l.type.includes(search);
      const matchStatus = statusFilter === 'all' || l.status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      let valA: number, valB: number;
      switch (sortField) {
        case 'views': valA = a.views; valB = b.views; break;
        case 'unlocks': valA = a.unlocks; valB = b.unlocks; break;
        case 'price': valA = a.price; valB = b.price; break;
        default: valA = new Date(a.listedAt).getTime(); valB = new Date(b.listedAt).getTime();
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === paginated.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map(l => l.id)));
    }
  };

  const handleDelete = (listing: OwnerListing) => {
    setListings(prev => prev.filter(l => l.id !== listing.id));
    setDeleteTarget(null);
    toast.success(`تم حذف "${listing.title}" بنجاح`);
  };

  const handleBulkDelete = () => {
    setListings(prev => prev.filter(l => !selected.has(l.id)));
    toast.success(`تم حذف ${selected.size} إعلان`);
    setSelected(new Set());
  };

  const handleTogglePause = (listing: OwnerListing) => {
    setListings(prev => prev.map(l => {
      if (l.id !== listing.id) return l;
      const newStatus: OwnerListingStatus = l.status === 'paused' ? 'published' : 'paused';
      toast.success(newStatus === 'paused' ? `تم إيقاف "${l.title}"` : `تم تفعيل "${l.title}"`);
      return { ...l, status: newStatus };
    }));
  };

  const handleStatusChange = (id: string, status: OwnerListingStatus) => {
    setListings(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    toast.success('تم تحديث حالة الإعلان');
  };

  const handleEdit = (updated: OwnerListing) => {
    setListings(prev => prev.map(l => l.id === updated.id ? updated : l));
    setEditTarget(null);
    toast.success('تم تحديث الإعلان بنجاح');
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={13} className="text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="text-primary-500" />
      : <ChevronDown size={13} className="text-primary-500" />;
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {/* Table Header Controls */}
        <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-100 flex-wrap">
          <h3 className="font-600 text-gray-900">إعلاناتي ({listings.length})</h3>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="ابحث في إعلاناتك..."
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                className="bg-gray-50 border border-gray-200 rounded-lg pr-9 pl-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 w-48 text-right"
              />
            </div>
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value as OwnerListingStatus | 'all'); setCurrentPage(1); }}
              className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="all">جميع الحالات</option>
              <option value="published">منشور</option>
              <option value="featured">مميز</option>
              <option value="draft">مسودة</option>
              <option value="paused">موقوف</option>
              <option value="expired">منتهي</option>
            </select>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selected.size > 0 && (
          <div className="flex items-center justify-between bg-primary-50 border-b border-primary-100 px-4 py-2.5 transition-all">
            <span className="text-sm font-600 text-primary-700">{selected.size} إعلان محدد</span>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-600 rounded-lg transition-colors"
            >
              <Trash2 size={13} />
              حذف المحدد
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.size === paginated.length && paginated.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-primary-500"
                  />
                </th>
                <th className="px-4 py-3 text-right text-xs font-600 text-gray-500 min-w-[240px]">الإعلان</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-gray-500">النوع</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-gray-500">الحالة</th>
                <th
                  className="px-4 py-3 text-right text-xs font-600 text-gray-500 cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('price')}
                >
                  <div className="flex items-center gap-1">السعر <SortIcon field="price" /></div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-600 text-gray-500 cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('views')}
                >
                  <div className="flex items-center gap-1"><Eye size={13} /> مشاهدات <SortIcon field="views" /></div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-600 text-gray-500 cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('unlocks')}
                >
                  <div className="flex items-center gap-1"><Unlock size={13} /> طلبات <SortIcon field="unlocks" /></div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-600 text-gray-500 cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('listedAt')}
                >
                  <div className="flex items-center gap-1"><Calendar size={13} /> تاريخ الإدراج <SortIcon field="listedAt" /></div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-600 text-gray-500">انتهاء الإعلان</th>
                <th className="px-4 py-3 text-xs font-600 text-gray-500 text-center">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                        <Search size={22} className="text-gray-400" />
                      </div>
                      <p className="text-sm font-600 text-gray-700">لا توجد إعلانات مطابقة</p>
                      <p className="text-xs text-gray-400">جرب تغيير الفلاتر أو أضف إعلاناً جديداً</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((listing, idx) => {
                  const statusCfg = STATUS_CONFIG[listing.status];
                  const isExpiringSoon = new Date(listing.expiresAt).getTime() - new Date().getTime() < 7 * 24 * 60 * 60 * 1000;
                  return (
                    <tr
                      key={listing.id}
                      className={`border-b border-gray-50 hover:bg-gray-50/70 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/30'} ${selected.has(listing.id) ? 'bg-primary-50/40' : ''}`}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(listing.id)}
                          onChange={() => toggleSelect(listing.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-500"
                        />
                      </td>

                      {/* Title + Image */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                            <AppImage
                              src={listing.image}
                              alt={listing.imageAlt}
                              fill
                              className="object-cover"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-600 text-gray-900 truncate max-w-[180px]">{listing.title}</p>
                            <p className="text-xs text-gray-400">{listing.city} — {listing.district}</p>
                          </div>
                        </div>
                      </td>

                      {/* Type + Mode */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-500 text-gray-700">{listing.type}</span>
                          <span className={`text-xs font-600 px-2 py-0.5 rounded-full w-fit ${listing.mode === 'sale' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                            {listing.mode === 'sale' ? 'للبيع' : 'للإيجار'}
                          </span>
                        </div>
                      </td>

                      {/* Status with inline change */}
                      <td className="px-4 py-3">
                        <div className="relative group/status">
                          <select
                            value={listing.status}
                            onChange={e => handleStatusChange(listing.id, e.target.value as OwnerListingStatus)}
                            className={`appearance-none text-xs font-600 px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none ${statusCfg.bg} ${statusCfg.color}`}
                          >
                            <option value="published">منشور</option>
                            <option value="featured">مميز</option>
                            <option value="draft">مسودة</option>
                            <option value="paused">موقوف</option>
                            <option value="expired">منتهي</option>
                          </select>
                        </div>
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-700 text-gray-900 tabular-nums whitespace-nowrap">
                          {listing.price >= 1000000
                            ? `${(listing.price / 1000000).toFixed(2)}م`
                            : listing.price >= 1000
                            ? `${(listing.price / 1000).toFixed(0)}ك`
                            : listing.price.toLocaleString('ar-SA')}
                        </span>
                        <span className="text-xs text-gray-400 block">{listing.priceUnit}</span>
                      </td>

                      {/* Views */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm font-600 text-gray-700 tabular-nums">
                          <Eye size={13} className="text-blue-400" />
                          {listing.views.toLocaleString('ar-SA')}
                        </div>
                      </td>

                      {/* Unlocks */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm font-600 tabular-nums">
                          <Unlock size={13} className="text-amber-500" />
                          <span className={listing.unlocks > 0 ? 'text-amber-700' : 'text-gray-400'}>
                            {listing.unlocks}
                          </span>
                        </div>
                      </td>

                      {/* Listed At */}
                      <td className="px-4 py-3 text-xs text-gray-500 tabular-nums whitespace-nowrap">
                        {new Date(listing.listedAt).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>

                      {/* Expires At */}
                      <td className="px-4 py-3">
                        <span className={`text-xs tabular-nums whitespace-nowrap font-500 ${isExpiringSoon ? 'text-red-600 font-600' : 'text-gray-500'}`}>
                          {isExpiringSoon && '⚠ '}
                          {new Date(listing.expiresAt).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ opacity: 1 }}>
                          {/* Feature */}
                          <button
                            onClick={() => handleStatusChange(listing.id, listing.status === 'featured' ? 'published' : 'featured')}
                            title={listing.status === 'featured' ? 'إلغاء التمييز' : 'تمييز الإعلان'}
                            className={`p-1.5 rounded-lg transition-colors ${listing.status === 'featured' ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' : 'bg-gray-100 text-gray-400 hover:bg-amber-50 hover:text-amber-500'}`}
                          >
                            <Star size={14} fill={listing.status === 'featured' ? 'currentColor' : 'none'} />
                          </button>
                          {/* Pause/Resume */}
                          <button
                            onClick={() => handleTogglePause(listing)}
                            title={listing.status === 'paused' ? 'تفعيل الإعلان' : 'إيقاف الإعلان'}
                            className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                          >
                            {listing.status === 'paused' ? <Play size={14} /> : <Pause size={14} />}
                          </button>
                          {/* Edit */}
                          <button
                            onClick={() => setEditTarget(listing)}
                            title="تعديل الإعلان"
                            className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                          >
                            <Pencil size={14} />
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => setDeleteTarget(listing)}
                            title="حذف الإعلان — لا يمكن التراجع"
                            className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              عرض {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} من {filtered.length}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs font-500 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                السابق
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={`mgmt-page-${page}`}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 text-xs font-600 rounded-lg transition-all ${currentPage === page ? 'bg-primary-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-xs font-500 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                التالي
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          listing={deleteTarget}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Edit Modal */}
      {editTarget && (
        <EditListingModal
          listing={editTarget}
          onSave={handleEdit}
          onClose={() => setEditTarget(null)}
        />
      )}
    </>
  );
}